/**
 * Civil (wall-clock) dates and times, with no timezone involved: the API already
 * speaks the user's wall clock ("2026-09-30" / "2026-09-30T18:00"), so the UI only
 * ever does calendar arithmetic. Dates are "YYYY-MM-DD" strings (sortable), times are
 * minutes since midnight. Internally Date.UTC is used purely as a day counter.
 */

export type DateStr = string; // YYYY-MM-DD
export type DateTimeStr = string; // YYYY-MM-DDTHH:mm

const pad = (n: number) => String(n).padStart(2, "0");

export function makeDate(y: number, m: number, d: number): DateStr {
  const t = new Date(Date.UTC(y, m - 1, d));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

export function parts(date: DateStr): { y: number; m: number; d: number } {
  return { y: Number(date.slice(0, 4)), m: Number(date.slice(5, 7)), d: Number(date.slice(8, 10)) };
}

function utc(date: DateStr): number {
  const { y, m, d } = parts(date);
  return Date.UTC(y, m - 1, d);
}

export function addDays(date: DateStr, n: number): DateStr {
  const { y, m, d } = parts(date);
  return makeDate(y, m, d + n);
}

export function addMonths(date: DateStr, n: number): DateStr {
  const { y, m, d } = parts(date);
  const last = daysInMonth(y, m + n);
  return makeDate(y, m + n, Math.min(d, last));
}

export function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** 0 = Sunday ... 6 = Saturday */
export function weekday(date: DateStr): number {
  return new Date(utc(date)).getUTCDay();
}

export function diffDays(a: DateStr, b: DateStr): number {
  return Math.round((utc(b) - utc(a)) / 86_400_000);
}

export function startOfWeek(date: DateStr, weekStart: number): DateStr {
  return addDays(date, -((weekday(date) - weekStart + 7) % 7));
}

export function startOfMonth(date: DateStr): DateStr {
  return date.slice(0, 8) + "01";
}

export function datePart(value: string): DateStr {
  return value.slice(0, 10);
}

/** Minutes since midnight of a DateTimeStr (0 for a bare date). */
export function minutesOf(value: string): number {
  if (value.length < 16) return 0;
  return Number(value.slice(11, 13)) * 60 + Number(value.slice(14, 16));
}

export function makeDateTime(date: DateStr, minutes: number): DateTimeStr {
  const dayShift = Math.floor(minutes / 1440);
  const m = ((minutes % 1440) + 1440) % 1440;
  return `${addDays(date, dayShift)}T${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
}

/** Absolute minute count, for comparing/subtracting DateTimeStr values. */
export function absMinutes(value: string): number {
  return diffDays("2000-01-01", datePart(value)) * 1440 + minutesOf(value);
}

export function fromAbsMinutes(total: number): DateTimeStr {
  return makeDateTime("2000-01-01", total);
}

export function formatTime(minutes: number): string {
  const m = ((minutes % 1440) + 1440) % 1440;
  return `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
}

export function rangeDays(start: DateStr, count: number): DateStr[] {
  return Array.from({ length: count }, (_, i) => addDays(start, i));
}

// ---------------------------------------------------------------------------
// pt-BR names (fixed tables: formatting must not depend on the browser's timezone)
// ---------------------------------------------------------------------------

export const WEEKDAYS_LONG = ["domingo", "segunda-feira", "terça-feira", "quarta-feira", "quinta-feira", "sexta-feira", "sábado"];
export const WEEKDAYS_SHORT = ["dom.", "seg.", "ter.", "qua.", "qui.", "sex.", "sáb."];
export const WEEKDAYS_LETTER = ["D", "S", "T", "Q", "Q", "S", "S"];
export const MONTHS_LONG = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
export const MONTHS_SHORT = ["jan.", "fev.", "mar.", "abr.", "mai.", "jun.", "jul.", "ago.", "set.", "out.", "nov.", "dez."];

export function monthYearLabel(date: DateStr): string {
  const { y, m } = parts(date);
  return `${MONTHS_LONG[m - 1]} de ${y}`;
}

/** "segunda-feira, 28 de setembro" */
export function longDateLabel(date: DateStr, withYear = false): string {
  const { y, m, d } = parts(date);
  return `${WEEKDAYS_LONG[weekday(date)]}, ${d} de ${MONTHS_LONG[m - 1]}${withYear ? ` de ${y}` : ""}`;
}

/** "seg., 28 de set. de 2026" */
export function shortDateLabel(date: DateStr): string {
  const { y, m, d } = parts(date);
  return `${WEEKDAYS_SHORT[weekday(date)]}, ${d} de ${MONTHS_SHORT[m - 1]} de ${y}`;
}

/** Title for a range of days: "setembro de 2026", "set. – out. de 2026", "dez. de 2026 – jan. de 2027". */
export function rangeTitle(first: DateStr, last: DateStr): string {
  const a = parts(first);
  const b = parts(last);
  if (a.y === b.y && a.m === b.m) return monthYearLabel(first);
  if (a.y === b.y) return `${MONTHS_SHORT[a.m - 1]} – ${MONTHS_SHORT[b.m - 1]} de ${a.y}`;
  return `${MONTHS_SHORT[a.m - 1]} de ${a.y} – ${MONTHS_SHORT[b.m - 1]} de ${b.y}`;
}

/** Duration label for the end-time picker: "30 min", "1 h", "1,5 h". */
export function durationLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = minutes / 60;
  return `${Number.isInteger(h) ? h : h.toFixed(2).replace(/0+$/, "").replace(".", ",")} h`;
}

// ---------------------------------------------------------------------------
// "Now" in the user's timezone
// ---------------------------------------------------------------------------

/** Wall clock in `timeZone` at `at` (default: now). */
export function nowIn(timeZone: string, at: Date = new Date()): { date: DateStr; minutes: number } {
  const map: Record<string, string> = {};
  for (const p of new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).formatToParts(at)) {
    map[p.type] = p.value;
  }
  return { date: `${map.year}-${map.month}-${map.day}`, minutes: Number(map.hour) * 60 + Number(map.minute) };
}
